// Résumé / experience page. Printable, copy-friendly.
import { Link } from "react-router-dom";
import { ArrowLeft, Printer } from "@phosphor-icons/react";

const SECTIONS = [
  {
    title: "Coaching & Fitness",
    items: [
      { role: "Founder & Head Coach", org: "Sweat With Slay", when: "2025 — present", bullets: [
        "Online strength-first coaching delivered through Everfit",
        "Tiered service offering: Essentials / Coaching / VIP / Bespoke",
        "Programming for busy professionals, returners, athletes, travelers",
      ] },
      { role: "Personal Trainer", org: "Big-box commercial gym", when: "2024 — 2025 · 1+ year", bullets: [
        "1:1 strength & conditioning, mobility, programming",
        "Foundation in client assessment, periodization, progressive overload",
      ] },
    ],
  },
  {
    title: "Tech & Business",
    items: [
      { role: "Enterprise Tech Sales", org: "Full-time", when: "Ongoing", bullets: [
        "Long-cycle B2B SaaS selling into Fortune 500 buyers",
        "Heavy travel; cross-functional with product, marketing, customer success",
      ] },
      { role: "Board Member", org: "Tampa Bay Tech", when: "Current", bullets: [
        "Tampa Bay's leading tech industry organization",
        "Programming, mentorship, ecosystem development",
      ] },
      { role: "Member", org: "Innovators Council", when: "Current", bullets: [
        "Community of operators building product, software and businesses",
      ] },
      { role: "HOA Board", org: "Community leadership", when: "Current", bullets: [
        "Governance, budgeting, conflict resolution, community ops",
      ] },
    ],
  },
  {
    title: "Athletics & Performance",
    items: [
      { role: "Division I Lacrosse — Athlete", org: "Iona College", when: "Collegiate", bullets: [
        "D1 athletic background; injury recovery rebuilt training approach",
      ] },
      { role: "Active in", org: "Pickleball · Golf · Swimming · Cycling · Rowing", when: "Ongoing", bullets: [
        "Annual: Tunnel to Towers tower climb (NYC) — tribute to Uncle Harry",
      ] },
    ],
  },
  {
    title: "Creative / Music",
    items: [
      { role: "DJ — \"SlayJ\"", org: "Tampa / private events", when: "Ongoing", bullets: [
        "DJ identity from which the Sweat With Slay brand originated",
      ] },
    ],
  },
  {
    title: "Education",
    items: [
      { role: "Bachelor of Business Administration (BBA)", org: "Iona College", when: "Graduated", bullets: [] },
    ],
  },
  {
    title: "Certifications & Insurance",
    items: [
      { role: "NASM Certified Personal Trainer (CPT)", org: "NASM", when: "Current", bullets: [] },
      { role: "NFPT Certified Personal Trainer", org: "NFPT", when: "Current", bullets: [] },
      { role: "Certified Nutrition Coach (CNC)", org: "NASM", when: "Current", bullets: [] },
      { role: "CPR / AED — Adult, Child & Infant", org: "American Safety Training Institute (ASTI)", when: "Current", bullets: [] },
      { role: "Liability Insured", org: "Ergo Next", when: "Active", bullets: [] },
    ],
  },
];

export default function Resume() {
  return (
    <main data-testid="resume-page" className="font-body bg-white text-[#0A0A0A] min-h-screen">
      {/* Toolbar — hidden on print */}
      <div className="print:hidden bg-[#0A0A0A] text-white px-6 md:px-12 py-4 flex flex-wrap items-center justify-between gap-3 border-b-4 border-[#FF1B91]">
        <Link to="/" data-testid="resume-back" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] font-bold hover:text-[#FF1B91] transition-colors">
          <ArrowLeft size={14} weight="bold" /> Back to site
        </Link>
        <button
          onClick={() => window.print()}
          data-testid="resume-print"
          className="inline-flex items-center gap-2 bg-[#FF1B91] hover:bg-[#FF3FA3] text-white px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em]"
        >
          <Printer size={14} weight="bold" /> Print / Save as PDF
        </button>
      </div>

      <article className="max-w-3xl mx-auto px-6 md:px-12 py-10 md:py-14 print:py-6">
        {/* Header */}
        <header className="border-b-4 border-[#FF1B91] pb-6 mb-10">
          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl">
            AJ <span className="font-brush font-normal normal-case text-[#FF1B91]">&ldquo;SlayJ&rdquo;</span> Johnson
          </h1>
          <p className="mt-3 text-base md:text-lg text-[#0A0A0A]/80 leading-snug">
            Founder &amp; Coach — Sweat With Slay · Enterprise Tech Sales · DJ · Board Member, Tampa Bay Tech
          </p>
          <p className="mt-2 text-sm text-[#0A0A0A]/55 uppercase tracking-[0.25em] font-semibold">
            Tampa, FL · NY roots · Coaching everywhere
          </p>
          <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-sm text-[#0A0A0A]/75">
            <a href="https://sweatwithslay.com" className="hover:text-[#FF1B91]">sweatwithslay.com</a>
            <a href="https://www.linkedin.com/in/alannajohnsonbd/" target="_blank" rel="noreferrer noopener" className="hover:text-[#FF1B91]">linkedin.com/in/alannajohnsonbd</a>
            <a href="https://instagram.com/sweatwithslay" target="_blank" rel="noreferrer noopener" className="hover:text-[#FF1B91]">@sweatwithslay</a>
          </div>
        </header>

        {/* Summary */}
        <section className="mb-10">
          <h2 className="text-[10px] uppercase tracking-[0.4em] text-[#FF1B91] font-bold mb-3">Summary</h2>
          <p className="text-base leading-relaxed text-[#0A0A0A]/85">
            Operator across tech, fitness, and community. Full-time enterprise tech sales professional. Founder and head coach of Sweat With Slay — strength-first online coaching delivered through Everfit. Board member at Tampa Bay Tech, member of the Innovators Council, HOA board contributor, performing DJ (SlayJ), and former Division I lacrosse athlete. Triple-certified (NASM CPT, NFPT CPT, NASM CNC), CPR/AED-trained, and liability insured.
          </p>
        </section>

        {/* Sections */}
        {SECTIONS.map((sec) => (
          <section key={sec.title} className="mb-9">
            <h2 className="text-[10px] uppercase tracking-[0.4em] text-[#FF1B91] font-bold mb-4 border-t-2 border-[#0A0A0A]/15 pt-4">
              {sec.title}
            </h2>
            <div className="space-y-5">
              {sec.items.map((it, i) => (
                <div key={i} className="break-inside-avoid">
                  <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <p className="font-semibold text-base">{it.role}{it.org && <span className="text-[#0A0A0A]/70 font-normal"> · {it.org}</span>}</p>
                    {it.when && <p className="text-xs uppercase tracking-[0.2em] text-[#0A0A0A]/55 font-semibold">{it.when}</p>}
                  </div>
                  {it.bullets && it.bullets.length > 0 && (
                    <ul className="mt-1.5 pl-5 list-disc space-y-1 text-sm text-[#0A0A0A]/80 leading-relaxed">
                      {it.bullets.map((b, j) => <li key={j}>{b}</li>)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))}

        {/* Footer */}
        <footer className="mt-10 pt-6 border-t-2 border-[#0A0A0A]/15 text-xs uppercase tracking-[0.3em] text-[#0A0A0A]/55 font-semibold">
          Last updated: {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </footer>
      </article>
    </main>
  );
}
